import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

const OfferSection = ({ initialData = [], onChange }) => {
  // Offer structure as JSON objects
  const [offers, setOffers] = useState(initialData);

  const [formData, setFormData] = useState({
    regularPrice: "",
    regularUnit: "yr",
    salePrice: "",
    saleUnit: "yr",
    extraText: "",
  });

  const [editingId, setEditingId] = useState(null);

  // If parent changes initialData later, sync it
  useEffect(() => {
    setOffers(initialData);
  }, [initialData]);

  // Helper to update state + parent
  const syncOffers = (updatedOffers) => {
    setOffers(updatedOffers);
    if (onChange) {
      onChange(updatedOffers); // send back to parent page
    }
  };

  // Handle input change
  const handleOfferChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOfferDelete = (id) => {
    Swal.fire({
      title: "Delete this offer?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const updated = offers.filter((offer) => offer.id !== id);
        syncOffers(updated);
        toast.success("Offer deleted");
      }
    });
  };

  const handleOfferEdit = (offer) => {
    setEditingId(offer.id);
    setFormData({
      regularPrice: offer.regularPrice,
      regularUnit: offer.regularUnit,
      salePrice: offer.salePrice,
      saleUnit: offer.saleUnit,
      extraText: offer.extraText,
    });
  };

  const handleOfferSubmit = () => {
    if (!formData.regularPrice || !formData.salePrice) {
      toast.error("Please enter both regular and sale price.");
      return;
    }

    if (editingId) {
      // Update existing
      const updated = offers.map((offer) =>
        offer.id === editingId ? { ...offer, ...formData } : offer
      );
      syncOffers(updated);
      toast.success("Offer updated");
    } else {
      // Add new
      const newOffer = {
        id: Date.now(),
        ...formData,
      };
      const updated = [...offers, newOffer];
      syncOffers(updated);
      toast.success("Offer added");
    }

    // Reset form
    setFormData({
      regularPrice: "",
      regularUnit: "yr",
      salePrice: "",
      saleUnit: "yr",
      extraText: "",
    });
    setEditingId(null);
  };

  const handleOfferCancel = () => {
    setEditingId(null);
    setFormData({
      regularPrice: "",
      regularUnit: "yr",
      salePrice: "",
      saleUnit: "yr",
      extraText: "",
    });
  };

  return (
    <>
      {/* Offer cards */}
      <div className="row">
        {offers.length === 0 && <p>No offers added yet.</p>}

        {offers.map((offer) => (
          <div className="col-md-4 mb-3" key={offer.id}>
            <div className="card h-100">
              <div className="card-body d-flex flex-column justify-content-between">
                <div>
                  <p className="mb-1 text-muted">
                    Regular:
                    <span className="fw-bold">
                      {" "}
                      {offer.regularPrice}/{offer.regularUnit}
                    </span>
                  </p>
                  <p className="mb-1">
                    <span className="text-decoration-line-through me-1">
                      {offer.regularPrice}/{offer.regularUnit}
                    </span>
                    <span className="fw-bold">
                      {offer.salePrice}/{offer.saleUnit}
                    </span>
                  </p>
                  {offer.extraText && (
                    <p className="mb-0 text-success">{offer.extraText}</p>
                  )}
                </div>

                <div className="mt-3 d-flex justify-content-between">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    type="button"
                    onClick={() => handleOfferEdit(offer)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleOfferDelete(offer.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Form */}
      <div className="col-12 mt-3">
        <div className="row g-3">
          {/* Regular Price */}
          <div className="col-md-3">
            <label className="form-label">Regular Price</label>
            <input
              type="text"
              className="form-control"
              name="regularPrice"
              value={formData.regularPrice}
              onChange={handleOfferChange}
              placeholder="2999"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Regular Unit</label>
            <input
              type="text"
              className="form-control"
              name="regularUnit"
              value={formData.regularUnit}
              onChange={handleOfferChange}
              placeholder="yr / month"
            />
          </div>

          {/* Sale Price */}
          <div className="col-md-3">
            <label className="form-label">Sale Price</label>
            <input
              type="text"
              className="form-control"
              name="salePrice"
              value={formData.salePrice}
              onChange={handleOfferChange}
              placeholder="1999"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Sale Unit</label>
            <input
              type="text"
              className="form-control"
              name="saleUnit"
              value={formData.saleUnit}
              onChange={handleOfferChange}
              placeholder="yr / month"
            />
          </div>

          {/* Extra Text */}
          <div className="col-12">
            <label className="form-label">Extra Text</label>
            <input
              type="text"
              className="form-control"
              name="extraText"
              value={formData.extraText}
              onChange={handleOfferChange}
              placeholder="+ 12 months free (fully editable)"
            />
          </div>
        </div>

        <div className="mt-3 d-flex gap-2">
          <button
            type="button"
            onClick={handleOfferSubmit}
            className="btn btn-primary"
          >
            {editingId ? "Update Offer" : "Add Offer"}
          </button>
          {editingId && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleOfferCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default OfferSection;
